/**
 * The questions the room has asked, one at a time for the host: the guests' chat lines that ask something, which one
 * is up, which were put aside, and which are done. Pure, so the queue is tested on its own; `useQuestions` keeps its
 * state.
 */
import { useCallback, useMemo, useState } from "react";
import type { ChatLine } from "../../shared/live-protocol";
import { asksSomething } from "../../shared/live-questions";

/** A guest's line that asks something, oldest first: the host's own lines and hidden ones never count. */
export function questionsIn(lines: readonly ChatLine[]): ChatLine[] {
  return lines.filter((l) => !l.host && !l.mine && asksSomething(l.body));
}

export interface Queue {
  /** Every question asked so far, in the order asked. */
  all: ChatLine[];
  /** The ones not done and not put aside. */
  open: ChatLine[];
  /** The one in front of the host, or null when nothing waits. */
  current: ChatLine | null;
  /** The current question's place among all of them, from 1. */
  place: number;
  skipped: ChatLine[];
  /** Open questions after the current one, in order. */
  upNext: ChatLine[];
}

/** `at` is the question the host picked or moved to; it falls back to the first open one when that one is gone. */
export function queueOf(all: readonly ChatLine[], skipped: ReadonlySet<string>, done: ReadonlySet<string>, at: string | null): Queue {
  const open = all.filter((q) => !skipped.has(q.id) && !done.has(q.id));
  const current = open.find((q) => q.id === at) ?? open[0] ?? null;
  return {
    all: [...all],
    open,
    current,
    place: current ? all.findIndex((q) => q.id === current.id) + 1 : 0,
    skipped: all.filter((q) => skipped.has(q.id) && !done.has(q.id)),
    upNext: open.filter((q) => q.id !== current?.id),
  };
}

/** The question after `id` among the open ones, round to the first at the end. */
export function afterQuestion(open: readonly ChatLine[], id: string | null): string | null {
  if (open.length === 0) return null;
  const i = open.findIndex((q) => q.id === id);
  return open[(i + 1) % open.length].id;
}

/**
 * The host's questions. Answer live pins the line so everyone sees it, which is how the session report counts a
 * question answered in the room; Done lifts the pin.
 */
export function useQuestions(lines: readonly ChatLine[], pinnedId: string | null, pin: (id: string | null) => void) {
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set());
  const [done, setDone] = useState<ReadonlySet<string>>(new Set());
  const [at, setAt] = useState<string | null>(null);
  // Why kept here: the host sees "Answering live" at their own tap, without waiting for the room to say the line is pinned.
  const [mine, setMine] = useState<string | null>(null);
  const asked = useMemo(() => questionsIn(lines), [lines]);
  const queue = queueOf(asked, skipped, done, at);
  const current = queue.current;
  const answering = !!current && (pinnedId === current.id || mine === current.id);

  const add = (set: ReadonlySet<string>, id: string) => new Set(set).add(id);
  const answerLive = useCallback(() => {
    if (!current) return;
    pin(current.id);
    setMine(current.id);
  }, [current, pin]);
  const finish = useCallback(() => {
    if (!current) return;
    pin(null);
    setMine(null);
    setDone((was) => add(was, current.id));
  }, [current, pin]);
  const skip = useCallback(() => {
    if (!current) return;
    setSkipped((was) => add(was, current.id));
    setAt(null);
  }, [current]);
  const next = useCallback(() => setAt(afterQuestion(queue.open, current?.id ?? null)), [queue.open, current]);
  const bringBack = useCallback(() => {
    setSkipped(new Set());
    setAt(null);
  }, []);

  return { queue, answering, answerLive, finish, skip, next, pick: setAt, bringBack };
}
